import {createFeatureSelector, createSelector} from '@ngrx/store';
import {FeatureKeys} from '../app.reducer';
import {Dictionary, Predicate} from '@ngrx/entity';

import * as fromReducer from './employee.reducer'
import * as fromAdapter from './employee.adapter';
import {IEmployee} from '@signal/asp-data-commons';
import { ISRDetail } from '@signal/asp-data-commons/lib/interfaces/employee';

/* Selectors from the entity adapter */
export const {
  selectAll,
  selectEntities
} = fromReducer.adapter.getSelectors();

/* Selectors based on the feature state */
export const selectEmployeesFeature = createFeatureSelector<fromReducer.EmployeeState>(FeatureKeys.EMPLOYEES);

/* To select the appointments loading flag in the dashboard state */
export const selectEmployeesLoading = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.loading
);

export const selectEmployees = createSelector(
  selectEmployeesFeature,
  selectAll
);
export const selectEmployeesEntity = createSelector(
  selectEmployeesFeature,
  selectEntities
);

export const selectUpdateEmployeeLoading = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.loading
);

export const selectCurrentEmployeeId = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.employeeSelectedId
);

export const selectCurrentEmployee = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.entities[state.employeeSelectedId]
);

/* -------------------- TEAMS ---------------------------------*/

/* To select employees who are passing the predicate */
export const selectEmployeesByPredicate = createSelector(
  selectEmployees,
  (employees: IEmployee[], props: { predicate: Predicate<IEmployee> }) => employees.filter(props.predicate)
);

/* To select employees count who are passing the predicate */
export const selectEmployeesCountByPredicate = createSelector(
  selectEmployeesByPredicate,
  (employees: IEmployee[], props: { predicate: Predicate<IEmployee> }) => employees.length
);

export const selectEmployeeForSchedule = createSelector(
  selectEmployees,
  (employees: IEmployee[], props: { predicate: Predicate<IEmployee> }) => employees.filter(props.predicate).map(x => { return <ISRDetail>{id: x.id, name: x.displayName}})
);

/* To select employees by employee ids */
export const selectEmployeesByIds = createSelector(
  selectEmployeesEntity,
  (employees: Dictionary<IEmployee>, props: { employeeIds: string[] }) => props.employeeIds?.map(eId => employees[eId])?.filter(x=> x?.isActive) || []
);

/* To select flat rate hours of the given ids */
export const selectFlatRateHoursByIds = createSelector(
  selectEmployeesByIds,
  (employees: IEmployee[], props: { employeeIds: string[] }) => fromAdapter.employeeIdsToFlatRateHoursProjector(employees)
);

export const selectActiveEmployees = createSelector(
  selectEmployees,
  (employees : IEmployee[]) => employees.filter(x => x.isActive === true)
);
export const selectActiveVisibleEmployees = createSelector(
  selectActiveEmployees,
  (employees : IEmployee[]) => employees.filter(x => x.visibility === true && x.jobCode === "ADVISOR"&& x.department==='SERVICE' ).length
);
export const selectActiveAdvisorsCount = createSelector(
  selectActiveEmployees,
  (employees : IEmployee[]) =>employees.filter(x => x.jobCode === "ADVISOR"&& (x.department==='SERVICE')).length
);

export const selectActiveSalesAdvisorsCount = createSelector(
  selectActiveEmployees,
  (employees : IEmployee[]) =>employees.filter(x => x.jobCode === "ADVISOR"&& (x.department==='SALES')).length
);

export const selectActivePartsAdvisorsCount = createSelector(
  selectActiveEmployees,
  (employees : IEmployee[]) => employees.filter(x => x.jobCode === "ADVISOR" && (x.department === 'PARTS')).length
);

export const selectActiveTechniciansCount = createSelector(
  selectActiveEmployees,
  (employees : IEmployee[]) =>employees.filter(x => x.jobCode === "TECHNICIAN").length
);

export const selectInActiveEmployees = createSelector(
  selectEmployees,
  (employees : IEmployee[]) => employees.filter(x => x.isActive === false)
);

/* ------------ Staff master sync ---------- */
export const selectSyncStatus = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.staffMasterSyncStatus.data.status
);

export const selectIsSyncStatusLoading = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.staffMasterSyncStatus.loading
);


export const selectSyncStatusLastSyncedAt = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.staffMasterSyncStatus.data.lastSyncedAt
);

export const selectPreSignedUrl = createSelector(
  selectEmployeesFeature,
  (state: fromReducer.EmployeeState) => state.preSignedUrl
);
export const selectEmployeeIdsByPredicate = createSelector(
  selectEmployeesByPredicate,
  (employees: IEmployee[]) => employees.map(x=> x.id)
);
export const selectValidTxmTeamByEmployeesIds = createSelector(
  selectEmployeesByIds,
  (employees: IEmployee[], props: { employeeIds: string[] }) => employees.filter(x=> x.jobCode === 'TECHNICIAN').length < 2
);